import { Capacitor, registerPlugin } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';
import type { LngLat } from './types.ts';

/**
 * Where GPS fixes come from.
 *
 * Two sources, one shape:
 *   - NATIVE  (iOS/Android app): a background location service that keeps
 *     running with the phone locked and in a pocket. This is what makes a
 *     4-hour round work without the golfer babysitting the screen.
 *   - WEB     (browser, buggy head unit): navigator.geolocation. Fine there,
 *     because a head unit is powered and its screen stays on.
 *
 * The browser cannot do background location at all — iOS Safari suspends
 * timers and the geolocation watch the moment the screen locks. That is a
 * platform limit, not a tuning problem, and it is the whole reason the phone
 * build has to be a real app rather than a web page.
 */

export interface RawFix {
  pos: LngLat;
  accuracy: number;
  speed: number;
  heading: number | null;
  ts: number;
}

export type FixHandler = (fix: RawFix) => void;
export type ErrorHandler = (message: string) => void;

export interface LocationWatch {
  stop: () => Promise<void>;
}

// --- native plugin surface -------------------------------------------------

interface BgLocation {
  latitude: number;
  longitude: number;
  accuracy: number;
  speed: number | null;
  bearing: number | null;
  time: number | null;
}

interface BackgroundGeolocationPlugin {
  addWatcher(
    options: {
      backgroundMessage?: string;
      backgroundTitle?: string;
      requestPermissions?: boolean;
      stale?: boolean;
      distanceFilter?: number;
    },
    callback: (position?: BgLocation, error?: { code: string; message: string }) => void
  ): Promise<string>;
  removeWatcher(options: { id: string }): Promise<void>;
  openSettings(): Promise<void>;
}

const BackgroundGeolocation = registerPlugin<BackgroundGeolocationPlugin>('BackgroundGeolocation');

export const isNative = () => Capacitor.isNativePlatform();

// --- injected fixes (CarPlay) ----------------------------------------------

/**
 * Fixes pushed into the page by a native host that owns CoreLocation itself.
 *
 * The CarPlay window's webview is NOT the Capacitor bridge webview, so the
 * native plugins above don't exist there — and navigator.geolocation would
 * raise WebKit's own '"…" would like to use your location' prompt on the car
 * screen, which cannot be answered while driving. The host declares itself
 * before any page script runs (WKUserScript sets __GOCADDY_CARPLAY__) and
 * pushes every CLLocation in through __gocaddyInjectFix. The ?carplay=1 query
 * flag is the same contract declared a second way, so a lost user script and
 * a lost query string have to happen together before this silently regresses
 * to the prompting path.
 */
export interface InjectedFix {
  lat: number;
  lng: number;
  acc?: number;
  speed?: number;
  heading?: number | null;
  ts?: number;
}

declare global {
  interface Window {
    __GOCADDY_CARPLAY__?: boolean;
    __gocaddyInjectFix?: (f: InjectedFix) => void;
  }
}

export const isCarPlayHost = () =>
  window.__GOCADDY_CARPLAY__ === true ||
  new URLSearchParams(location.search).has('carplay');

function watchViaInjection(onFix: FixHandler): () => void {
  window.__gocaddyInjectFix = (f) => {
    gpsCounts.web += 1;
    onFix({
      pos: [f.lng, f.lat],
      accuracy: f.acc ?? 999,
      speed: Math.max(f.speed ?? 0, 0),
      heading: f.heading ?? null,
      ts: f.ts ?? Date.now(),
    });
  };
  return () => {
    delete window.__gocaddyInjectFix;
  };
}

/**
 * Distance filter, in metres.
 *
 * A golfer needs fine resolution — the yardage should change as they walk to
 * their ball — but waking the GPS for every 20cm of GPS jitter while standing
 * over a putt is what flattens a battery in 4 hours. 4m is below a single
 * pace's worth of meaningful movement while still suppressing noise.
 */
const DISTANCE_FILTER_M = 4;

/**
 * Per-source delivery counters.
 *
 * Which watcher is actually feeding the app is the single most useful fact when
 * the tile goes stale, and it was invisible: both sources call the same
 * handler, so "no fix" looked identical whether one had died or both had. These
 * are surfaced on screen and written to the widget's container, so the state of
 * the GPS can be read off a phone that is locked on a desk.
 */
export const gpsCounts = { bg: 0, cl: 0, web: 0, lastBg: 0, lastCl: 0, rearms: 0 };
export const noteRearm = () => { gpsCounts.rearms += 1; };

/**
 * CoreLocation via @capacitor/geolocation — the second native source.
 *
 * This used to be navigator.geolocation, which was the wrong tool on a phone
 * for one concrete reason: WKWebView asks for location in its OWN right, so the
 * golfer got a second '"localhost" would like to use your location' prompt on
 * top of the one iOS had already shown for GoCaddy. Two prompts for one
 * permission reads as a broken app, and it forced this watcher to sit dormant
 * for 10s rather than run from the start.
 *
 * The Capacitor plugin talks to the same CLLocationManager the app already has
 * permission for, so there is no second prompt and no reason to hold it back.
 * It runs alongside the background watcher from the first moment: that one owns
 * the locked screen, this one is instant and dependable while the app is up,
 * and useGps takes whichever arrives.
 */
function watchViaCoreLocation(onFix: FixHandler, onError: ErrorHandler): () => void {
  let id: string | null = null;
  let cancelled = false;

  void Geolocation.watchPosition({ enableHighAccuracy: true, timeout: 20_000 }, (p, err) => {
    if (err) {
      onError((err as Error)?.message ?? String(err));
      return;
    }
    if (!p) return;
    gpsCounts.cl += 1;
    gpsCounts.lastCl = Date.now();
    onFix({
      pos: [p.coords.longitude, p.coords.latitude],
      accuracy: p.coords.accuracy ?? 999,
      speed: Math.max(p.coords.speed ?? 0, 0),
      heading: p.coords.heading,
      ts: p.timestamp,
    });
  })
    .then((wid) => {
      if (cancelled) void Geolocation.clearWatch({ id: wid });
      else id = wid;
    })
    .catch((e) => onError(`CoreLocation watch failed: ${(e as Error)?.message ?? e}`));

  return () => {
    cancelled = true;
    if (id) void Geolocation.clearWatch({ id });
    id = null;
  };
}

/** A plain WebView geolocation watch. Browsers and head units only. */
function watchViaWebView(onFix: FixHandler, onError: ErrorHandler): () => void {
  if (!('geolocation' in navigator)) {
    onError('This device has no GPS receiver.');
    return () => {};
  }
  const id = navigator.geolocation.watchPosition(
    (p) => {
      gpsCounts.web += 1;
      onFix({
        pos: [p.coords.longitude, p.coords.latitude],
        accuracy: p.coords.accuracy ?? 999,
        speed: Math.max(p.coords.speed ?? 0, 0),
        heading: p.coords.heading,
        ts: p.timestamp,
      });
    },
    (e) => onError(e.message),
    { enableHighAccuracy: true, maximumAge: 1000, timeout: 20_000 }
  );
  return () => navigator.geolocation.clearWatch(id);
}

export async function startWatch(
  onFix: FixHandler,
  onError: ErrorHandler
): Promise<LocationWatch> {
  if (isCarPlayHost()) {
    const stop = watchViaInjection(onFix);
    return { stop: async () => stop() };
  }
  if (isNative()) {
    /*
     * Both native sources, from the first moment.
     *
     * They fail in different ways, which is exactly why both run. The
     * background watcher is the only thing that survives a locked screen, but
     * it can sit completely silent — a documented iOS bug has it stop emitting
     * after about an hour backgrounded, and it can also come up mute waiting on
     * an "Always" grant. CoreLocation via the Capacitor plugin is instant and
     * reliable while the app is up, but is not a background service.
     *
     * Neither prompts twice now, so there is nothing to gain by staggering
     * them. useGps takes whichever fix lands first.
     */
    const stopCore = watchViaCoreLocation(onFix, onError);
    let id: string | null = null;
    try {
      id = await BackgroundGeolocation.addWatcher(
      {
        // Android requires a visible notification for a foreground location
        // service. Being honest in it is also the decent thing to do.
        backgroundTitle: 'GoCaddy is tracking your round',
        backgroundMessage: 'Distances keep updating with your phone in your pocket.',
        requestPermissions: true,
        // Never hand the app a cached fix from a previous round.
        stale: false,
        distanceFilter: DISTANCE_FILTER_M,
      },
      (position, error) => {
        if (error) {
          if (error.code === 'NOT_AUTHORIZED') {
            onError(
              'Location permission is off. GoCaddy needs "Always" to keep ' +
                'measuring with the phone in your pocket.'
            );
          } else {
            onError(error.message);
          }
          return;
        }
        if (!position) return;
        gpsCounts.bg += 1;
        gpsCounts.lastBg = Date.now();
        onFix({
          pos: [position.longitude, position.latitude],
          accuracy: position.accuracy,
          speed: Math.max(position.speed ?? 0, 0),
          heading: position.bearing,
          ts: position.time ?? Date.now(),
        });
      }
      );
    } catch (e) {
      // The background plugin is unavailable or refused. CoreLocation is
      // already running, so carry on rather than going dark — the app just
      // loses the locked-screen case, which is worth saying out loud.
      onError(`background GPS unavailable: ${(e as Error)?.message ?? e}`);
    }
    const watcherId = id;
    return {
      stop: async () => {
        stopCore();
        if (watcherId) await BackgroundGeolocation.removeWatcher({ id: watcherId });
      },
    };
  }

  const stop = watchViaWebView(onFix, onError);
  return { stop: async () => stop() };
}

/** Deep-links to the OS location settings when permission has been denied. */
export async function openLocationSettings() {
  if (isNative()) await BackgroundGeolocation.openSettings();
}
